import React, { useState, useCallback, useRef, useEffect, memo } from "react";
import {
  PdfLoader,
  PdfHighlighter,
  Tip,
  Highlight,
  AreaHighlight,
  Popup,
  IHighlight,
  NewHighlight,
} from "react-pdf-highlighter";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import "react-pdf-highlighter/dist/style.css";

/**
 * Alpha applied to the chosen swatch, as a hex pair.
 *
 * The backend renders the highlight with a reduced-alpha overlay, so this is
 * what the final PDF shows. Both the in-progress selection and the committed
 * highlight read this one constant — that is what makes the colour you drag
 * identical to the colour you drop.
 */
const HIGHLIGHT_ALPHA_HEX = "66";
/** Smallest dragged box (either side, viewport px) kept as an area highlight. */
const MIN_AREA_HIGHLIGHT_PX = 8;

interface PDFHighlighterProps {
  pdfUrl: string;
  onDownload?: (highlights: IHighlight[]) => void;
  onHighlightsChange?: (highlights: IHighlight[]) => void;
  initialHighlights?: IHighlight[];
  currentColor: string;
}

const HighlightPopup = ({
  comment,
  highlightId,
}: {
  comment: { text: string; emoji: string };
  highlightId: string;
}) => {
  return (
    <Box
      className="Highlight__popup"
      sx={{
        backgroundColor: "transparent !important",
        background: "transparent !important",
        padding: "0 !important",
        border: "none !important",
        boxShadow: "none !important",
        borderRadius: "0 !important",
        minWidth: "auto !important",
        cursor: "pointer",
        "& *:not(button)": {
          backgroundColor: "transparent !important",
          background: "transparent !important",
        },
        "& button": {
          backgroundColor: "#e0e0e0 !important",
          background: "#e0e0e0 !important",
        },
      }}
    >
      {comment.text ? (
        <>
          {comment.emoji} {comment.text}
        </>
      ) : (
        <Box
          sx={{
            width: "8px",
            height: "8px",
            backgroundColor: "#e0e0e0",
            borderRadius: "50%",
            display: "inline-block",
          }}
        />
      )}
    </Box>
  );
};

const PDFHighlighter: React.FC<PDFHighlighterProps> = ({
  pdfUrl,
  onDownload,
  onHighlightsChange,
  initialHighlights = [],
  currentColor,
}) => {
  const [highlights, setHighlights] = useState<IHighlight[]>(initialHighlights);
  const [anchorEl, setAnchorEl] = useState<{
    element: HTMLElement | null;
    highlightId: string | null;
    position: { x: number; y: number } | null;
  }>({
    element: null,
    highlightId: null,
    position: null,
  });
  const scrollViewerTo = useRef<(highlight: IHighlight) => void>(() => {});
  const userSelectionRef = useRef<string>("");
  const lastHighlightTimeRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);
  const lastHighlightPositionRef = useRef<any>(null);
  // Scanned / flattened pages have no text layer, so text selection can't
  // highlight anything on them — a plain drag draws an area highlight there
  // instead. Filled in once per loaded document (see `probeTextLayer`).
  const imageOnlyPagesRef = useRef<Set<number>>(new Set());
  const probedDocumentRef = useRef<any>(null);

  // Sync highlights with initialHighlights when they change (e.g., when deleted from parent)
  // useEffect(() => {
  //   setHighlights(initialHighlights);
  // }, [initialHighlights]);

  const handleMenuClose = () => {
    setAnchorEl({ element: null, highlightId: null, position: null });
  };

  // Handle clicks on highlights to open dropdown menu
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is on a highlight element
      const highlightElement = target.closest("[data-highlight-id]");
      if (highlightElement) {
        const highlightId = highlightElement.getAttribute("data-highlight-id");
        if (highlightId) {
          e.stopPropagation();
          setAnchorEl({
            element: highlightElement as HTMLElement,
            highlightId,
            position: { x: e.clientX, y: e.clientY },
          });
        }
      } else if (!target.closest(".MuiMenu-root")) {
        // Close menu if clicking outside
        handleMenuClose();
      }
    };

    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  // Capture actual user selection before it gets processed
  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString().trim();

        // Only capture if there's actual text selected
        if (!selectedText || selectedText.length === 0) {
          userSelectionRef.current = "";
          return;
        }

        // AGGRESSIVE: Check if selection starts from whitespace
        const startContainer = range.startContainer;
        const startOffset = range.startOffset;

        if (startContainer.nodeType === Node.TEXT_NODE) {
          const textContent = startContainer.textContent || "";
          const textBefore = textContent.substring(0, startOffset);
          const textAtStart = textContent.substring(
            startOffset,
            Math.min(startOffset + 20, textContent.length),
          );

          // If text before selection is all whitespace, don't capture it
          if (textBefore.trim().length === 0 && textBefore.length > 0) {
            userSelectionRef.current = "";
            return;
          }

          // If text at start position is all whitespace, don't capture
          if (textAtStart.trim().length === 0 && textAtStart.length > 5) {
            userSelectionRef.current = "";
            return;
          }

          // If the text node is mostly whitespace, don't capture
          if (
            textContent.trim().length < textContent.length * 0.4 &&
            textContent.length > 10
          ) {
            userSelectionRef.current = "";
            return;
          }
        } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
          const element = startContainer as Element;
          const textContent = element.textContent || "";

          // If element is empty or mostly whitespace, don't capture
          if (
            textContent.trim().length === 0 ||
            (textContent.trim().length < textContent.length * 0.3 &&
              textContent.length > 5)
          ) {
            userSelectionRef.current = "";
            return;
          }

          // Check if it's a spacing element
          const tagName = element.tagName?.toLowerCase();
          if (
            (tagName === "br" || tagName === "div" || tagName === "p") &&
            textContent.trim().length < 3
          ) {
            userSelectionRef.current = "";
            return;
          }
        }

        // Check if selection spans multiple containers (might be paragraph span)
        const endContainer = range.endContainer;
        if (startContainer !== endContainer) {
          // Check if start or end is whitespace
          const startText =
            startContainer.nodeType === Node.TEXT_NODE
              ? startContainer.textContent || ""
              : (startContainer as Element).textContent || "";
          const endText =
            endContainer.nodeType === Node.TEXT_NODE
              ? endContainer.textContent || ""
              : (endContainer as Element).textContent || "";

          if (
            (startText.trim().length < startText.length * 0.3 &&
              startText.length > 5) ||
            (endText.trim().length < endText.length * 0.3 && endText.length > 5)
          ) {
            userSelectionRef.current = "";
            return;
          }
        }

        // Store the actual selected text only if it passed all checks
        userSelectionRef.current = selectedText;
      } else {
        // Clear if no selection
        userSelectionRef.current = "";
      }
    };

    // Capture selection on mouseup (when user finishes selecting) - use capture phase
    document.addEventListener("mouseup", handleSelection, true);
    document.addEventListener("selectionchange", handleSelection, true);

    // Also capture on mousedown to clear previous selection and track dragging
    const handleMouseDown = () => {
      userSelectionRef.current = "";
      isDraggingRef.current = false;
      lastHighlightPositionRef.current = null;
    };

    const handleMouseMove = () => {
      if (isDraggingRef.current === false) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
          isDraggingRef.current = true;
        }
      }
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("mousemove", handleMouseMove, true);

    return () => {
      document.removeEventListener("mouseup", handleSelection, true);
      document.removeEventListener("selectionchange", handleSelection, true);
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("mousemove", handleMouseMove, true);
    };
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .Highlight__popup {
        background-color: transparent !important;
        background: transparent !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        min-width: auto !important;
        overflow: hidden !important;
      }
      .Highlight__popup > * {
        background-color: transparent !important;
        background: transparent !important;
      }
      .Highlight__popup button {
        background-color: #e0e0e0 !important;
        background: #e0e0e0 !important;
      }
      .Highlight__popup button:hover {
        background-color: #bdbdbd !important;
        background: #bdbdbd !important;
      }
      .Highlight__popup:hover {
        overflow: hidden !important;
      }
      .Highlight__popup:hover * {
        overflow: hidden !important;
      }
      /* Remove scrollbars on hover */
      .Highlight__popup::-webkit-scrollbar {
        display: none !important;
      }
      .Highlight__popup {
        -ms-overflow-style: none !important;
        scrollbar-width: none !important;
      }
      /* Prevent automatic paragraph selection */
      .react-pdf-highlighter__container .textLayer {
        user-select: text !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
      }
      .react-pdf-highlighter__container .textLayer span {
        user-select: text !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        display: inline !important;
      }
      /* Prevent selection from expanding to entire paragraphs */
      .react-pdf-highlighter__container .textLayer br {
        pointer-events: none !important;
      }
      /* Prevent double-click from selecting entire paragraphs */
      .react-pdf-highlighter__container .textLayer {
        -webkit-touch-callout: default !important;
        -webkit-user-select: text !important;
      }
      /* Ensure proper text selection boundaries */
      .react-pdf-highlighter__container .textLayer div {
        display: inline !important;
      }
      /* Prevent selection from starting in whitespace between paragraphs */
      .react-pdf-highlighter__container .textLayer {
        line-height: 1.2 !important;
      }
      /* Make whitespace areas non-selectable */
      .react-pdf-highlighter__container .textLayer span:empty,
      .react-pdf-highlighter__container .textLayer div:empty {
        user-select: none !important;
        -webkit-user-select: none !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    const TAG_ID = "pdf-highlighter-selection-color";
    let style = document.getElementById(TAG_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = TAG_ID;
      document.head.appendChild(style);
    }
    // react-pdf-highlighter ships `.textLayer ::selection { background: <pale
    // yellow>; mix-blend-mode: multiply }` and puts no stable class on its own
    // container, so the override has to out-rank that rule directly rather than
    // scope itself under a wrapper. It also clears the blend mode, which the
    // committed `.Highlight__part` does not use — without that the drag preview
    // reads a different shade from the highlight it turns into.
    // `::selection` and `::-moz-selection` stay in separate rules: one invalid
    // pseudo-element in a selector list voids the whole rule.
    const fill = `${currentColor}${HIGHLIGHT_ALPHA_HEX}`;
    style.textContent = `
      .textLayer ::selection {
        background-color: ${fill} !important;
        mix-blend-mode: normal !important;
      }
      .textLayer ::-moz-selection {
        background-color: ${fill} !important;
        mix-blend-mode: normal !important;
      }
      .textLayer br::selection {
        background-color: transparent !important;
      }
      .textLayer br::-moz-selection {
        background-color: transparent !important;
      }
    `;
    return () => {
      const el = document.getElementById(TAG_ID);
      if (el) el.remove();
    };
  }, [currentColor]);

  const getNextId = () => String(Math.random()).slice(2);

  const isSamePosition = (a: any, b: any) => {
    if (!a || !b) return false;
    const brA = a.boundingRect;
    const brB = b.boundingRect;
    if (!brA || !brB) return false;
    if (a.pageNumber !== b.pageNumber) return false;
    return (
      brA.x1 === brB.x1 &&
      brA.y1 === brB.y1 &&
      brA.x2 === brB.x2 &&
      brA.y2 === brB.y2 &&
      brA.width === brB.width &&
      brA.height === brB.height
    );
  };

  const addHighlight = useCallback(
    (highlight: NewHighlight) => {
      const newHighlight: IHighlight = {
        ...highlight,
        id: getNextId(),
        comment: { ...highlight.comment, data: { color: currentColor } } as any,
      };
      setHighlights((prev) => {
        const exists = prev.some(
          (h) =>
            isSamePosition(h.position, newHighlight.position) &&
            (h.content?.text || "") === (newHighlight.content?.text || ""),
        );
        if (exists) {
          return prev;
        }
        return [...prev, newHighlight];
      });
      return newHighlight;
    },
    [currentColor],
  );

  const onHighlightsChangeRef = useRef(onHighlightsChange);
  useEffect(() => {
    onHighlightsChangeRef.current = onHighlightsChange;
  });
  useEffect(() => {
    onHighlightsChangeRef.current?.(highlights);
  }, [highlights]);

  const updateHighlight = useCallback(
    (highlightId: string, position: any, content: any) => {
      setHighlights(
        (prev) =>
          prev.map((h) =>
            h.id === highlightId
              ? {
                  ...h,
                  position: { ...h.position, ...position },
                  content: { ...h.content, ...content },
                }
              : h,
          ),
      );
    },
    [],
  );

  const deleteHighlight = useCallback((highlightId: string) => {
    setHighlights((prev) => prev.filter((h) => h.id !== highlightId));
  }, []);

  const clearAllHighlights = useCallback(() => {
    setHighlights([]);
  }, []);

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload(highlights);
    } else {
      // Default download behavior
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `document-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [highlights, onDownload, pdfUrl]);

  const handleDelete = useCallback(
    (highlightId: string) => {
      deleteHighlight(highlightId);
      handleMenuClose();
    },
    [deleteHighlight],
  );

  const highlightTransform = useCallback(
    (
      highlight: any,
      index: number,
      setTip: (
        highlight: any,
        callback: (highlight: any) => JSX.Element,
      ) => void,
      hideTip: () => void,
      viewportToScaled: (rect: any) => any,
      screenshot: (rect: any) => string,
      isScrolledTo: boolean,
    ) => {
      const isTextHighlight = !highlight.content.image;
      const highlightColor =
        (highlight.comment as any)?.data?.color || "#FF6062";
      const isHexColor = /^#([0-9A-Fa-f]{6})$/.test(highlightColor);
      const highlightFill = isHexColor
        ? `${highlightColor}${HIGHLIGHT_ALPHA_HEX}`
        : highlightColor;

      const component = isTextHighlight ? (
        <Box
          data-highlight-id={highlight.id}
          sx={{
            cursor: "pointer",
            width: "100%",
            height: "100%",
            position: "relative",
            "& .Highlight__part": {
              backgroundColor: `${highlightFill} !important`,
            },
          }}
        >
          <Highlight
            isScrolledTo={isScrolledTo}
            position={highlight.position}
            comment={highlight.comment}
          />
        </Box>
      ) : (
        <Box
          data-highlight-id={highlight.id}
          sx={{
            cursor: "pointer",
            width: "100%",
            height: "100%",
            position: "relative",
          }}
        >
          <AreaHighlight
            isScrolledTo={isScrolledTo}
            highlight={highlight}
            onChange={(boundingRect) => {
              updateHighlight(
                highlight.id,
                {
                  boundingRect: viewportToScaled(boundingRect),
                },
                { image: screenshot(boundingRect) },
              );
            }}
          />
        </Box>
      );
      return (
        <Popup
          popupContent={
            <HighlightPopup
              comment={highlight.comment as any}
              highlightId={highlight.id}
            />
          }
          onMouseOver={(popupContent) => setTip(highlight, () => popupContent)}
          onMouseOut={hideTip}
          key={highlight.id}
          children={component}
        />
      );
    },
    [updateHighlight],
  );

  const commitHighlight = useCallback(
    (position: any, content: any, hideTipAndSelection: () => void) => {
      // Clear the stored selection after use
      userSelectionRef.current = "";

      // Store the highlight position and time to prevent duplicates
      lastHighlightTimeRef.current = Date.now();
      lastHighlightPositionRef.current =
        position.boundingRect || (position.rects && position.rects[0]);
      isDraggingRef.current = false;

      addHighlight({
        content,
        position,
        comment: { text: "", emoji: "" },
      });
      // hideTipAndSelection from react-pdf-highlighter dismisses its own tip
      // UI but doesn't reliably clear the browser's text selection — the
      // selection's `::selection` style (40% alpha) then layers on top of the
      // freshly committed highlight (also 40%) until the user clicks
      // elsewhere, making it look temporarily darker. Force-clear the
      // selection here so the on-screen color matches the committed alpha
      // immediately.
      try {
        const selection = window.getSelection();
        selection?.removeAllRanges?.();
      } catch {
        // No-op: some browsers throw if selection is detached.
      }
      hideTipAndSelection();
      return null;
    },
    [addHighlight],
  );

  const onSelectionFinished = useCallback(
    (
      position: any,
      content: any,
      hideTipAndSelection: () => void,
      transformSelection: () => void,
    ) => {
      // Prevent multiple highlights from continuous dragging
      const now = Date.now();
      const timeSinceLastHighlight = now - lastHighlightTimeRef.current;

      // If a highlight was created very recently (within 1000ms), reject new highlights
      // This prevents creating multiple highlights when dragging continues
      if (timeSinceLastHighlight < 1000) {
        const lastPos = lastHighlightPositionRef.current;
        const currentPos =
          position.boundingRect || (position.rects && position.rects[0]);

        // Check if positions are close (within 50px) - likely same drag operation
        if (
          currentPos &&
          lastPos &&
          currentPos.x1 !== undefined &&
          lastPos.x1 !== undefined
        ) {
          const distanceX = Math.abs(currentPos.x1 - lastPos.x1);
          const distanceY = Math.abs((currentPos.y1 || 0) - (lastPos.y1 || 0));

          // If positions are close, reject to prevent duplicate highlights from dragging
          if (distanceX < 50 && distanceY < 50) {
            hideTipAndSelection();
            return null;
          }
        } else {
          // If we can't compare positions but it's very recent, reject to be safe
          if (timeSinceLastHighlight < 500) {
            hideTipAndSelection();
            return null;
          }
        }
      }

      // Validate that we have valid position and content
      if (!position || !content) {
        hideTipAndSelection();
        return null;
      }

      // Check if the selection is valid (has bounding rect or rects)
      const hasValidPosition =
        (position.boundingRect &&
          position.boundingRect.width > 0 &&
          position.boundingRect.height > 0) ||
        (position.rects &&
          Array.isArray(position.rects) &&
          position.rects.length > 0);

      if (!hasValidPosition) {
        hideTipAndSelection();
        return null;
      }

      // Area selections carry a screenshot, not text — the text-selection
      // checks below would reject every one of them. They're the only way to
      // highlight a scanned page, so commit them on position alone.
      if (content.image) {
        // The library accepts a 1px box, so a click with a slight wobble would
        // otherwise leave a speck of a highlight behind.
        const { width = 0, height = 0 } = position.boundingRect || {};
        if (width < MIN_AREA_HIGHLIGHT_PX || height < MIN_AREA_HIGHLIGHT_PX) {
          hideTipAndSelection();
          return null;
        }
        return commitHighlight(position, content, hideTipAndSelection);
      }

      // Validate content text is not empty or just whitespace
      const text = content?.text?.trim() || "";
      if (!text) {
        hideTipAndSelection();
        return null;
      }

      // AGGRESSIVE CHECK: Detect if selection started from whitespace between paragraphs
      try {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const startContainer = range.startContainer;
          const endContainer = range.endContainer;
          const startOffset = range.startOffset;
          const endOffset = range.endOffset;

          // Check if selection starts from whitespace
          let startsFromWhitespace = false;

          if (startContainer.nodeType === Node.TEXT_NODE) {
            const textContent = startContainer.textContent || "";
            const textBefore = textContent.substring(0, startOffset);
            const textAtStart = textContent.substring(
              startOffset,
              Math.min(startOffset + 10, textContent.length),
            );

            // If text before selection is all whitespace, it starts from whitespace
            if (textBefore.trim().length === 0 && textBefore.length > 0) {
              startsFromWhitespace = true;
            }

            // If text at start is all whitespace, reject
            if (textAtStart.trim().length === 0 && textAtStart.length > 0) {
              startsFromWhitespace = true;
            }

            // If the entire text node is mostly whitespace, reject
            if (
              textContent.trim().length < textContent.length * 0.3 &&
              textContent.length > 5
            ) {
              startsFromWhitespace = true;
            }
          } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
            const element = startContainer as Element;
            const textContent = element.textContent || "";

            // Empty element or element with only whitespace
            if (textContent.trim().length === 0) {
              startsFromWhitespace = true;
            }

            // Check if element is a line break or spacing element
            const tagName = element.tagName?.toLowerCase();
            if (tagName === "br" || tagName === "div" || tagName === "p") {
              if (
                textContent.trim().length === 0 ||
                textContent.trim().length < 3
              ) {
                startsFromWhitespace = true;
              }
            }
          }

          // Check if selection spans across what looks like paragraph boundaries
          const selectedText = range.toString().trim();
          const contentText = content.text?.trim() || "";

          // If selection starts from whitespace, reject immediately
          if (startsFromWhitespace) {
            hideTipAndSelection();
            return null;
          }

          // Check bounding rect to detect whitespace selections
          const boundingRect = range.getBoundingClientRect();
          // If the selection has very small height or width, it might be from whitespace
          if (
            boundingRect.height < 5 ||
            (boundingRect.width < 10 && boundingRect.height < 15)
          ) {
            // Very small selection - likely from whitespace
            hideTipAndSelection();
            return null;
          }

          // If no actual text was selected but content exists, reject
          if (!selectedText && contentText) {
            hideTipAndSelection();
            return null;
          }

          // Check if start and end containers are different (might indicate paragraph span)
          if (startContainer !== endContainer) {
            // Selection spans multiple containers - check if it's valid
            const startText =
              startContainer.nodeType === Node.TEXT_NODE
                ? startContainer.textContent || ""
                : (startContainer as Element).textContent || "";
            const endText =
              endContainer.nodeType === Node.TEXT_NODE
                ? endContainer.textContent || ""
                : (endContainer as Element).textContent || "";

            // If start or end is mostly whitespace, reject
            if (
              (startText.trim().length < startText.length * 0.3 &&
                startText.length > 5) ||
              (endText.trim().length < endText.length * 0.3 &&
                endText.length > 5)
            ) {
              hideTipAndSelection();
              return null;
            }
          }

          // If content text is longer and doesn't closely match selected text, reject
          if (
            selectedText &&
            contentText &&
            contentText.length > selectedText.length * 1.1
          ) {
            const normalizedSelected = selectedText
              .replace(/\s+/g, " ")
              .toLowerCase();
            const normalizedContent = contentText
              .replace(/\s+/g, " ")
              .toLowerCase();

            // If content doesn't start with selected text, it's likely expanded from whitespace
            const matchLength = Math.min(30, normalizedSelected.length);
            if (
              matchLength > 0 &&
              !normalizedContent.startsWith(
                normalizedSelected.substring(0, matchLength),
              )
            ) {
              hideTipAndSelection();
              return null;
            }
          }
        } else {
          // No selection range - reject if content exists
          if (content.text && content.text.trim().length > 0) {
            hideTipAndSelection();
            return null;
          }
        }
      } catch (error) {
        console.warn("Selection validation error:", error);
        // On error, reject if content is suspiciously long
        if (content.text && content.text.length > 100) {
          hideTipAndSelection();
          return null;
        }
      }

      // STRICT VALIDATION: Get the actual user selection and compare with content
      // Reject any selection where content doesn't closely match user's actual selection
      try {
        // Use the captured user selection
        const actualSelectedText = userSelectionRef.current;

        // Also try to get current selection as fallback
        const selection = window.getSelection();
        let currentSelectedText = "";
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          currentSelectedText = range.toString().trim();
        }

        // Use the most recent selection
        const selectedText = actualSelectedText || currentSelectedText;
        const contentText = content.text?.trim() || "";

        // ULTRA STRICT: If no user selection was captured, reject if content is long
        if (!selectedText || selectedText.length === 0) {
          if (contentText.length > 50) {
            // No selection but large content - definitely auto-expanded
            hideTipAndSelection();
            return null;
          }
        } else if (contentText) {
          // Normalize whitespace for comparison
          const normalizedSelected = selectedText
            .replace(/\s+/g, " ")
            .toLowerCase();
          const normalizedContent = contentText
            .replace(/\s+/g, " ")
            .toLowerCase();

          // Calculate word counts
          const selectedWords = normalizedSelected
            .split(/\s+/)
            .filter((w: string) => w.length > 0);
          const contentWords = normalizedContent
            .split(/\s+/)
            .filter((w: string) => w.length > 0);
          const wordRatio =
            contentWords.length / Math.max(selectedWords.length, 1);

          // Calculate length ratio
          const lengthRatio =
            normalizedContent.length / Math.max(normalizedSelected.length, 1);

          // ULTRA STRICT VALIDATION: Reject if content has significantly more words
          // This catches paragraph expansion even if length is similar
          if (wordRatio > 1.2) {
            // Content has many more words - reject auto-expanded selection
            hideTipAndSelection();
            return null;
          }

          // ULTRA STRICT VALIDATION: Reject if content is more than 5% longer
          // This prevents automatic paragraph expansion
          if (lengthRatio > 1.05) {
            // Content is longer - reject auto-expanded selection
            hideTipAndSelection();
            return null;
          }

          // Check if content starts with selected text (must match at start)
          const selectedStart = normalizedSelected.substring(
            0,
            Math.min(50, normalizedSelected.length),
          );
          if (
            selectedStart.length > 0 &&
            !normalizedContent.startsWith(selectedStart)
          ) {
            // Content doesn't start with selection - reject
            hideTipAndSelection();
            return null;
          }

          // Reject if content has more words (even 10% more)
          if (wordRatio > 1.1) {
            hideTipAndSelection();
            return null;
          }

          // Reject if content length is more than 5% longer
          if (normalizedContent.length > normalizedSelected.length * 1.05) {
            hideTipAndSelection();
            return null;
          }

          // Ensure the first few words match exactly (to catch paragraph expansion)
          const minWords = Math.min(
            5,
            selectedWords.length,
            contentWords.length,
          );
          if (minWords > 0) {
            const selectedStartWords = selectedWords
              .slice(0, minWords)
              .join(" ");
            const contentStartWords = contentWords.slice(0, minWords).join(" ");
            if (selectedStartWords !== contentStartWords) {
              // First words don't match - likely expanded
              hideTipAndSelection();
              return null;
            }
          }

          // Additional check: if selected text is short but content is long, reject
          if (
            normalizedSelected.length < 20 &&
            normalizedContent.length > normalizedSelected.length * 1.02
          ) {
            hideTipAndSelection();
            return null;
          }
        }

        // Safety check: reject any very long content without matching selection
        if (contentText.length > 200) {
          if (!selectedText || selectedText.length < contentText.length * 0.9) {
            hideTipAndSelection();
            return null;
          }
        }
      } catch (error) {
        // If validation fails, be conservative and reject long content
        if (content.text && content.text.length > 100) {
          hideTipAndSelection();
          return null;
        }
        console.warn("Selection validation error:", error);
      }

      return commitHighlight(position, content, hideTipAndSelection);
    },
    [commitHighlight],
  );

  /**
   * Records which pages have no text layer, once per loaded document. Only
   * flips state-free refs, so it's safe to kick off from PdfLoader's render
   * callback; `enableAreaSelection` reads the ref on each mousedown.
   */
  const probeTextLayer = (pdfDocument: any) => {
    if (probedDocumentRef.current === pdfDocument) return;
    probedDocumentRef.current = pdfDocument;
    const imageOnlyPages = new Set<number>();
    imageOnlyPagesRef.current = imageOnlyPages;
    (async () => {
      for (
        let pageNumber = 1;
        pageNumber <= pdfDocument.numPages;
        pageNumber++
      ) {
        try {
          const page = await pdfDocument.getPage(pageNumber);
          const textContent = await page.getTextContent();
          const hasText = textContent.items.some(
            (item: any) => typeof item.str === "string" && item.str.trim(),
          );
          if (!hasText) imageOnlyPages.add(pageNumber);
        } catch {
          // Leave the page on text selection if it can't be read.
        }
      }
    })();
  };

  const isAreaSelectionStart = (event: MouseEvent) => {
    if (event.altKey) return true;
    const target = event.target as Element | null;
    // Pressing on an existing highlight is for its menu / moving it, not for
    // drawing a new one over the top. The area class is CSS-module hashed.
    if (target?.closest?.('.Highlight, [class*="_areaHighlight_"]')) {
      return false;
    }
    const page = target?.closest?.(".page[data-page-number]");
    const pageNumber = Number(page?.getAttribute("data-page-number"));
    return imageOnlyPagesRef.current.has(pageNumber);
  };
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Global Menu for all highlights */}
      <Menu
        open={Boolean(anchorEl.position && anchorEl.highlightId)}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          anchorEl.position
            ? { top: anchorEl.position.y, left: anchorEl.position.x }
            : undefined
        }
        PaperProps={{
          sx: {
            width: 150,
            mt: -0.5,
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            py: 0.5,
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          },
        }}
        MenuListProps={{
          sx: { py: 0 },
        }}
      >
        {anchorEl.highlightId && (
          <MenuItem
            onClick={() => handleDelete(anchorEl.highlightId!)}
            sx={{
              px: 1.5,
              py: 0.75,
              "&:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
              <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#374151" }}
            >
              Delete
            </Typography>
          </MenuItem>
        )}
      </Menu>
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          position: "relative",
          "& .react-pdf-highlighter__container": {
            height: "100%",
          },
        }}
      >
        <PdfLoader
          url={pdfUrl}
          beforeLoad={
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading PDF...</Typography>
            </Box>
          }
        >
          {(pdfDocument) => {
            probeTextLayer(pdfDocument);
            return (
              <PdfHighlighter
                pdfDocument={pdfDocument}
                enableAreaSelection={isAreaSelectionStart}
                onScrollChange={() => {}}
                scrollRef={(scrollTo) => {
                  scrollViewerTo.current = scrollTo;
                }}
                onSelectionFinished={onSelectionFinished}
                highlightTransform={highlightTransform}
                highlights={highlights}
              />
            );
          }}
        </PdfLoader>
      </Box>
    </Box>
  );
};

export default memo(PDFHighlighter);
